// React
import { useEffect, useState } from "react"

// Components
import { Button, HeaderMenu, Loading, Select } from "components"

// Utils
import { useLocation } from "utils"
import FinanceMonthlyTable from "./components/FinanceMonthlyTable"
import { MonthlyApis, useLFinanceMonthlyList } from "./utils/FinanceMonthlyList"
import { useFinanceYearlyList, yearlyApis } from "./utils/FinanceYearlyList"
import { FinanceMonthlyType } from "./types/FinanceMonthlyType"
import FinanceYearlyTable from "./components/FinanceYearlyTable"
import { KPIApis, mapToKPIEntityRow, useFinanceKPIEntityList } from "./utils/FinanceKpiList"
import FinanceKPIEntityTable from "./components/KpiTable"
import { KPIEntityRow } from "./types/FinanceKpiTypes"

export default function Finance(): JSX.Element {

  const location = useLocation()

  const [year, setYear] = useState<string | undefined>()
  const [locationId, setLocationId] = useState<number | "">("")
  const [tableData, setTableData] = useState<FinanceMonthlyType[]>([])
  const [isUpdated, setIsUpdated] = useState(false)
  const [rows, setRows] = useState<KPIEntityRow[]>([])
  

  // const isFilterReady = Boolean(year)

  const currentYear = new Date().getFullYear()

  const yearOptions = Array.from({ length: 10 }, (_, i) => {
    const y = currentYear - i
    return {
      label: String(y),
      value: String(y)  
    }
  })

  const {
    monthlyData,
    loading,
    refetchLiquidity
  } = useLFinanceMonthlyList(Number(year), String(locationId))

  const {
    yearlyData,
    loading: yearlyLoading,
    refetchYearly
  } = useFinanceYearlyList(Number(year), String(locationId))

  const { kpiEntityData, loadingKpi, refetchKPIEntity } = useFinanceKPIEntityList(Number(year), String(locationId))
  

  useEffect(() => {
    if (monthlyData.length > 0) {
      setTableData(monthlyData)
    } else {
      setTableData(emptyMonthlyData)
    }
    // eslint-disable-next-line
  }, [monthlyData])

    useEffect(() => {
        if (kpiEntityData && kpiEntityData.length > 0) {
        const mapped = kpiEntityData.map(mapToKPIEntityRow)
        setRows(mapped)
        } else {
        const mappedEmpty = emptyKPIEntityData.map(mapToKPIEntityRow)
        setRows(mappedEmpty)
        }
        // eslint-disable-next-line
    }, [kpiEntityData, location.data])

  
    function createEmptyMonthlyData(keys: {
        key_description: string
        code_budget: string
        category: string
    }[]) {
        const months = [
        "january","february","march","april","may","june",
        "july","august","september","october","november","december"
        ]
    
        return keys.map((key) => ({
        ...key,
        budget: "",
        average: "",
        ...Object.fromEntries(months.map((m) => [m, null]))
        }))
    }

    const createEmptyYearlyData = (keys: typeof yearlyApis) => {
        const currentYear = new Date().getFullYear()
    
        const years = Array.from({ length: 6 }, (_, i) => currentYear - i)
    
        return keys.map((key) => ({
        ...key,
        data: years.map((year) => ({
            year,
            budget_id: null,
            budget: "",
            actual: ""
        }))
        }))
    }
  
    const emptyMonthlyData = createEmptyMonthlyData(MonthlyApis) as FinanceMonthlyType[]
    const emptyYearlyData = createEmptyYearlyData(yearlyApis)

    const createEmptyKPIEntityData = (
        keys: {
        key_description: string
        code_budget: string
        }[],
        locations: { label: string; value: string | number }[]
    ) => {
        return keys.map((key) => {
        const locationValues = Object.fromEntries(
            locations.map((loc) => [loc.label, "0"])
        )

        return {
            key_description: key.key_description,
            code_budget: key.code_budget,
            budget: "",
            average: "",
            ...locationValues
        }
        })
    }

    const emptyKPIEntityData = createEmptyKPIEntityData(
        KPIApis,
        location.data ?? []
      )

  return (
    <section className="flex flex-col gap-3">

      <HeaderMenu title="FINANCE" />

      {/* FILTER */}
      <section className="flex flex-wrap justify-end gap-3">

        <div className="max-w-[200px] w-full">
          <Select
            placeholder="Select Year"
            options={yearOptions}
            value={yearOptions.find(opt => opt.value === year)}
            onChange={(val) =>
              setYear(val?.value ? String(val.value) : undefined)
            }
          />
        </div>

        <div className="max-w-[200px] w-full">
          <Select
            isLoading={location.isLoading}
            options={[
              { label: "ALL", value: "" },
              ...(location.data ?? [])
            ]}
            value={
              [
                { label: "ALL", value: "" },
                ...(location.data ?? [])
              ].find((opt) => opt.value === locationId)
            }
            onChange={(val) => {
              if (!val?.value) {
                setLocationId("")
                return
              }

              setLocationId(Number(val.value))
            }}
          />
        </div>
        
        <Button
          color="primary"
          disabled={!year}
          onClick={() => {
            refetchLiquidity()
            refetchYearly()
            setIsUpdated(true)
            refetchKPIEntity()
          }}
        >
          UPDATE
        </Button>

      </section>

      {/* TABLE */}
      {loading ? (
        <Loading loading />
      ) : (
        <FinanceMonthlyTable
          list={tableData}
          setList={setTableData}
          year={Number(year)}
          locationId={locationId === "" ? null : locationId}
          isFilterReady={isUpdated}
        />
      )}

      {/* TABLE */}
      {yearlyLoading ? (
        <Loading loading />
      ) : (
        <section className="mt-6">
          <FinanceYearlyTable
            list={
              yearlyData.length > 0
                ? yearlyData
                : emptyYearlyData
            }
            isFilterReady={isUpdated}
          />
        </section>
      )}

      {/* TABLE */}
      {loadingKpi ? (
        <Loading loading />
      ) : (
        <section className="mt-6">
            <FinanceKPIEntityTable
                list={rows}
                year={Number(year)}
                locationId={locationId === "" ? null : locationId}
                isFilterReady={isUpdated}
                setList={setRows}
            />
        </section>
      )}

    </section>
  )
}